import React, { useState } from 'react';
import {
  DocPage, PageHeader, Section, Prerequisites,
  Para, Callout,
} from '../components/DocBlocks';
import { useIndustry } from '../IndustryContext';
import './Proration.css';

const TOC = [
  { id: 'overview',  label: 'Overview' },
  { id: 'fixed',     label: 'Fixed price rate card' },
  { id: 'composite', label: 'Fixed + usage rate card' },
  { id: 'calculator', label: 'Calculator' },
];

// ── Timeline bar ──────────────────────────────────────────────────────────────

function Timeline({ daysInCycle, startDay }) {
  const daysActive = daysInCycle - startDay + 1;
  const inactivePct = ((startDay - 1) / daysInCycle) * 100;
  const activePct   = (daysActive / daysInCycle) * 100;

  return (
    <div className="pro-timeline">
      <div className="pro-timeline-bar">
        <div className="pro-timeline-inactive" style={{ width: `${inactivePct}%` }} />
        <div className="pro-timeline-active"   style={{ width: `${activePct}%` }}>
          <span className="pro-timeline-label">{daysActive}d active</span>
        </div>
      </div>
      <div className="pro-timeline-ticks">
        <span>Day 1</span>
        {startDay > 1 && startDay < daysInCycle && (
          <span style={{ marginLeft: `${inactivePct}%` }}>Day {startDay}</span>
        )}
        <span>Day {daysInCycle}</span>
      </div>
    </div>
  );
}

// ── Proration calculator ──────────────────────────────────────────────────────

function ProrationCalc({ currency, defaultFee }) {
  const [fixedFee,    setFixedFee]    = useState(defaultFee);
  const [daysInCycle, setDaysInCycle] = useState(30);
  const [startDay,    setStartDay]    = useState(16);

  const safeStart  = Math.min(Math.max(1, startDay), daysInCycle);
  const daysActive = daysInCycle - safeStart + 1;
  const prorated   = ((fixedFee * daysActive) / daysInCycle);
  const pct        = ((daysActive / daysInCycle) * 100).toFixed(1);

  const fmt = (n) => {
    const v = Number(n);
    return `${currency}${v % 1 === 0 ? v : parseFloat(v.toFixed(2))}`;
  };

  return (
    <div className="pro-calc">
      <div className="pro-calc-inputs">
        <label className="pro-field">
          <span className="pro-field-label">Fixed fee ({currency} / cycle)</span>
          <input className="pro-input" type="number" min="0" value={fixedFee}
            onChange={(e) => setFixedFee(Number(e.target.value))} />
        </label>
        <label className="pro-field">
          <span className="pro-field-label">Days in billing cycle</span>
          <input className="pro-input" type="number" min="1" max="365" value={daysInCycle}
            onChange={(e) => setDaysInCycle(Math.max(1, Number(e.target.value)))} />
        </label>
        <label className="pro-field">
          <span className="pro-field-label">Contract starts on day</span>
          <input className="pro-input" type="number" min="1" max={daysInCycle} value={startDay}
            onChange={(e) => setStartDay(Number(e.target.value))} />
        </label>
      </div>

      <Timeline daysInCycle={daysInCycle} startDay={safeStart} />

      <div className="pro-calc-result">
        <div className="pro-result-formula">
          {fmt(fixedFee)} × ({daysActive} / {daysInCycle}) = <strong>{fmt(prorated)}</strong>
        </div>
        <div className="pro-result-pct">{pct}% of the full cycle charge</div>
      </div>
    </div>
  );
}

// ── Composite breakdown ───────────────────────────────────────────────────────

function CompositeBreakdown({ currency, fixedFee, rate, unit, daysActive, daysInCycle, usage }) {
  const proratedFixed = (fixedFee * daysActive) / daysInCycle;
  const usageCharge   = usage * rate;
  const total         = proratedFixed + usageCharge;

  const fmt = (n) => {
    const v = Number(n);
    return `${currency}${v % 1 === 0 ? v : parseFloat(v.toFixed(2))}`;
  };

  return (
    <div className="pro-composite">
      <div className="pro-composite-row pro-composite-row--prorated">
        <div className="pro-composite-component">
          <span className="pro-composite-label">Fixed base fee</span>
          <span className="pro-composite-detail">
            {fmt(fixedFee)} × ({daysActive}/{daysInCycle} days) — <strong>prorated</strong>
          </span>
        </div>
        <span className="pro-composite-amount">{fmt(proratedFixed)}</span>
      </div>
      <div className="pro-composite-row">
        <div className="pro-composite-component">
          <span className="pro-composite-label">Usage charge</span>
          <span className="pro-composite-detail">
            {usage} {unit} × {fmt(rate)} — <strong>not prorated</strong>
          </span>
        </div>
        <span className="pro-composite-amount">{fmt(usageCharge)}</span>
      </div>
      <div className="pro-composite-total">
        <span>Total</span>
        <span>{fmt(total)}</span>
      </div>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function Proration({ onNavigate }) {
  const { industry } = useIndustry();
  const { proration: pro, rateCard: rc } = industry;
  const currency = rc.currency;

  const fmt = (n) => {
    const v = Number(n);
    return `${currency}${v % 1 === 0 ? v : parseFloat(v.toFixed(2))}`;
  };

  return (
    <DocPage toc={TOC}>
      <PageHeader
        eyebrow="Billing · Rate Card"
        title="Proration"
        description="Proration scales a fixed fee to the fraction of the billing period the contract was active — so a customer who joins mid-period isn't billed for days before they started."
      />

      <Prerequisites
        onNavigate={onNavigate}
        items={[
          { id: 'rate-card', label: 'Rate Cards' },
        ]}
      />

      <Section id="overview" title="Overview">
        <Para>
          A billing cycle is typically a full calendar month. When a contract starts or ends
          mid-cycle, the customer should only be charged for the portion of the period they were
          active. Proration handles this automatically — but it is opt-in. A rate card with
          proration disabled always charges the full amount regardless of when in the cycle the
          contract became active.
        </Para>
        <Callout type="info" title="Which rate card types support proration">
          Proration applies to <strong>Fixed Price</strong> rate cards, the{' '}
          <strong>fixed base fee</strong> within a composite Fixed + Usage rate card, and any{' '}
          <strong>flat fee defined on a tier</strong> in a Graduated or Volume rate card.
          Pure per-unit charges (Rate Per Unit, and the usage portion of Graduated / Volume)
          are never prorated — they are already scoped to actual consumption within the period.
        </Callout>
        <Callout type="warning" title="Min/Max rate cards are not prorated">
          The flat fee in a Min/Max rate card is a <strong>comparison threshold</strong>, not a
          standing charge. It is used to pick the higher or lower of two computed amounts —
          it is never scaled by days active. A Min/Max rate card always resolves using the
          full flat fee and full usage, regardless of when the contract started.
        </Callout>
      </Section>

      <Section id="fixed" title="Fixed price rate card">
        <Para>
          For a purely fixed rate card, the entire charge is the fixed amount. With proration
          enabled, that amount is scaled by the fraction of the cycle the contract was active:
        </Para>
        <div className="pro-formula-block">
          <span className="pro-formula">
            Prorated charge = Fixed fee × (Days active / Days in cycle)
          </span>
        </div>
        <Para>
          For example, a {fmt(pro.fixedFee)}/month rate card where the contract starts on
          day 16 of a 30-day month:
        </Para>
        <div className="pro-example-calc">
          <span>{fmt(pro.fixedFee)} × (15 / 30) = <strong>{fmt(pro.fixedFee / 2)}</strong></span>
        </div>
        <Para>
          Without proration enabled, the full {fmt(pro.fixedFee)} would be charged regardless
          of the start date.
        </Para>
      </Section>

      <Section id="composite" title="Fixed + usage rate card">
        <Para>
          A rate card can combine a fixed base fee with a usage-based charge — for example,{' '}
          {fmt(pro.compositeFixed)} per cycle plus {fmt(pro.compositeRate)} per{' '}
          {pro.compositeUnit}. When proration is enabled on this rate card, only the fixed
          base fee is prorated. The usage charge is calculated on actual usage recorded
          during the active period and is unaffected.
        </Para>
        <Para>
          Example: contract starts day 16 of a 30-day cycle, {42} {pro.compositeUnitPlural}
          consumed:
        </Para>
        <CompositeBreakdown
          currency={currency}
          fixedFee={pro.compositeFixed}
          rate={pro.compositeRate}
          unit={pro.compositeUnit}
          daysActive={15}
          daysInCycle={30}
          usage={42}
        />
        <Callout type="tip">
          This separation keeps the billing logic consistent: fixed fees compensate for
          availability over time, so they scale with time. Usage fees compensate for what
          was consumed, so they follow consumption.
        </Callout>
      </Section>

      <Section id="calculator" title="Calculator">
        <Para>
          Adjust the fixed fee, billing cycle length, and contract start day to see the
          prorated charge.
        </Para>
        <ProrationCalc currency={currency} defaultFee={pro.fixedFee} />
      </Section>
    </DocPage>
  );
}
